import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, CircularProgress, IconButton, InputAdornment, Paper, TextField, Typography } from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { notify } from '../notifications';
import { apiErrorMessage } from '../admin/reports/edit/editApi';
import { resetStaffPassword } from './staffApi';
import { IssuedPassword, StaffMember, StaffTarget } from './types';
import { passwordProblem } from './staffUtils';
import { primaryButtonSx, STAFF_PRIMARY } from './staffStyles';

interface StaffPasswordPanelProps {
  target: StaffTarget;
  member: StaffMember;
  /** Сервер сгенерировал пароль: его показывает общее окно раздела */
  onIssued: (issued: IssuedPassword) => void;
  onReset: () => void;
}

/** Сброс пароля: пустое поле - пароль генерирует сервер; все сессии сотрудника завершаются */
const StaffPasswordPanel: React.FC<StaffPasswordPanelProps> = ({ target, member, onIssued, onReset }) => {
  const [password, setPassword] = useState('');
  const [show, setShow] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPassword('');
    setShow(false);
  }, [member.id]);

  const problem = passwordProblem(password, member.login);
  const mustChange = member.kind === 'person';

  const handleReset = async () => {
    setSaving(true);
    try {
      const result = await resetStaffPassword(target, member.id, password);
      setPassword('');
      if (result.password) {
        onIssued({ login: member.login, fullName: member.fullName, password: result.password, mustChange });
      } else {
        notify.success(result.message);
      }
      onReset();
    } catch (err) {
      notify.error(apiErrorMessage(err, 'Не удалось сбросить пароль'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#ffffff', borderRadius: '16px', border: '1px solid #e2e8f0', boxShadow: '0 2px 10px rgba(0,0,0,0.03)' }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <KeyIcon sx={{ color: STAFF_PRIMARY, fontSize: 20 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0f172a' }}>Сброс пароля</Typography>
      </Box>
      <Typography variant="body2" sx={{ color: '#64748b', mb: 2 }}>
        {mustChange
          ? 'Сотрудник войдёт с новым паролем и сразу задаст свой'
          : 'Общая учётка: новый пароль действует до следующего сброса'}
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexDirection: { xs: 'column', sm: 'row' } }}>
        <TextField
          label="Новый пароль"
          type={show ? 'text' : 'password'}
          value={password}
          onChange={e => setPassword(e.target.value)}
          size="small"
          fullWidth
          disabled={saving}
          autoComplete="new-password"
          error={Boolean(problem)}
          helperText={problem || 'Оставьте пустым - пароль сгенерируется и будет показан один раз'}
          inputProps={{ maxLength: 128 }}
          InputProps={{
            sx: { bgcolor: '#f8fafc', borderRadius: '8px' },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShow(!show)} aria-label={show ? 'Скрыть пароль' : 'Показать пароль'}>
                  {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Box sx={{ flexShrink: 0 }}>
          <Button
            variant="contained"
            disableElevation
            onClick={handleReset}
            disabled={saving || Boolean(problem)}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={primaryButtonSx}
          >
            Сбросить пароль
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mt: 2, borderRadius: '10px' }}>
        После сброса все сессии сотрудника будут завершены
      </Alert>
    </Paper>
  );
};

export default StaffPasswordPanel;
